/**
 * Centralized color palette for ligand annotation categories.
 *
 * Used consistently across LigandAnnotationDonut, TopLigandsChart, and
 * LigandDistanceByAnnotation so that the same annotation always renders
 * with the same color throughout the page.
 *
 * Annotations not in the explicit map fall back to FALLBACK_PALETTE,
 * assigned deterministically by string hash so the same unknown
 * annotation always gets the same fallback color.
 */

// Explicit, curated palette for known annotations
export const ANNOTATION_COLORS = {
  "drug-like":     "#58c5bf", // turquoise
  "cofactor-like": "#5dade2", // blue
  "reactant-like": "#bb8fce", // purple
  "ion":           "#f4d03f", // yellow
  "modification":  "#58d68d", // green
  "unannotated":   "#bdbdbd", // gray
  "unknown":       "#bdbdbd", // gray (alias)
};

// Fallback palette for annotations not explicitly mapped above
const FALLBACK_PALETTE = [
  "#ec7063", "#f5b041", "#85c1e9", "#a3e4d7", "#d7bde2",
  "#f8c471", "#76d7c4", "#d2b4de", "#aed6f1",
];

/**
 * Returns the color for a given annotation string.
 * Lookup is case-insensitive and trims whitespace.
 * Unknown annotations get a deterministic fallback (same input -> same color).
 */
export const getAnnotationColor = (annotation) => {
  if (!annotation) return ANNOTATION_COLORS.unknown;

  const key = annotation.toString().trim().toLowerCase();

  if (key in ANNOTATION_COLORS) return ANNOTATION_COLORS[key];

  // Deterministic fallback: simple string hash → palette index
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
    hash |= 0;
  }
  return FALLBACK_PALETTE[Math.abs(hash) % FALLBACK_PALETTE.length];
};
